import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { formatPostDateShort } from "@/lib/date";
import AdminShell from "@/components/AdminShell";
import { toast } from "sonner";
import { Search, Trash2, Edit3, Plus, Wand2, Bookmark, Image, UserPlus, Shuffle } from "lucide-react";

const Tab = ({ active, children, ...rest }) => (
  <button
    {...rest}
    className={`px-4 py-2 font-sans text-xs tracking-[0.25em] uppercase border-b-2 transition-colors ${
      active ? "border-[#1a1918] text-[#1a1918]" : "border-transparent text-[#4a4843] hover:text-[#1a1918]"
    }`}
  >
    {children}
  </button>
);

const formatDate = formatPostDateShort;

const VALID_STATUSES = new Set(["draft", "published", "review", "reserved"]);

const AdminPosts = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatus = (() => {
    const s = searchParams.get("status");
    return VALID_STATUSES.has(s) ? s : "draft";
  })();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [status, setStatusState] = useState(initialStatus);
  const [stats, setStats] = useState({ total: 0, published: 0, drafts: 0, to_review: 0 });
  const [authorStats, setAuthorStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const perPage = 25;

  const setStatus = (next) => {
    setStatusState(next);
    const params = new URLSearchParams(searchParams);
    params.set("status", next);
    setSearchParams(params, { replace: true });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, statsResp] = await Promise.all([
        api.get("/admin/posts", { params: { q, status, page, per_page: perPage } }),
        api.get("/admin/stats"),
      ]);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setStats(statsResp.data);
    } finally {
      setLoading(false);
    }
  }, [q, status, page]);

  useEffect(() => { load(); }, [load]);

  // Author counter (only when viewing published posts)
  useEffect(() => {
    if (status !== "published") {
      setAuthorStats([]);
      return;
    }
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/stats/authors", { params: { status: "published" } });
        if (!cancel) setAuthorStats(data.items || []);
      } catch {
        if (!cancel) setAuthorStats([]);
      }
    })();
    return () => { cancel = true; };
  }, [status]);

  const setStateValue = async (id, next) => {
    try {
      await api.post(`/admin/posts/${id}/state`, { state: next });
      const labels = { draft: "Bozza", published: "Pubblicato", review: "Da controllare" };
      toast.success(labels[next]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo post?")) return;
    try {
      await api.delete(`/admin/posts/${id}`);
      toast.success("Eliminato");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const reformatAllDrafts = async () => {
    if (!window.confirm(`Riformattare i capoversi di TUTTE le ${stats.drafts} bozze? Ogni riga diventerà un paragrafo. Operazione non reversibile.`)) return;
    try {
      const { data } = await api.post("/admin/reformat", {});
      toast.success(`${data.processed} bozze riformattate`);
      load();
    } catch (e) {
      toast.error("Errore riformattazione");
    }
  };

  const autoCoverAll = async () => {
    if (!window.confirm("Assegnare una copertina casuale (Lorem Picsum, stabile per ogni post) a tutti i post che non ne hanno?")) return;
    const tid = toast.loading("assegnazione copertine…");
    try {
      const { data } = await api.post("/admin/auto-cover", {});
      toast.success(`${data.updated} copertine assegnate`, { id: tid });
      load();
    } catch (e) {
      toast.error("Errore", { id: tid });
    }
  };

  const bulkAuthor = async () => {
    const name = window.prompt("Imposta l'autore per i post che non ne hanno:\n(scrivi il nome esatto, poi conferma)");
    if (!name || !name.trim()) return;
    const trimmed = name.trim();
    const overwriteAll = window.confirm(
      `Vuoi SOSTITUIRE l'autore in TUTTI i post con "${trimmed}"?\n\n` +
      `• OK = sovrascrive l'autore di tutti i post\n` +
      `• Annulla = imposta "${trimmed}" SOLO sui post senza autore`
    );
    const tid = toast.loading("aggiornamento autori…");
    try {
      const { data } = await api.post("/admin/bulk-author", {
        author: trimmed,
        only_missing: !overwriteAll,
      });
      toast.success(`${data.updated} post aggiornati`, { id: tid });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore", { id: tid });
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const randomizeDates = async () => {
    const matchDate = window.prompt(
      "Randomizza la data dei post pubblicati che hanno una specifica data\n(formato YYYY-MM-DD; default: 2026-05-01).\nLe nuove date saranno casuali fra 2021-01-01 e oggi.",
      "2026-05-01"
    );
    if (matchDate === null) return;
    const m = (matchDate || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(m)) {
      return toast.error("Formato data non valido");
    }
    if (!window.confirm(`Confermi? Tutti i post PUBBLICATI con data ${m} verranno spostati a una data casuale fra 01/01/2021 e oggi.`)) return;
    const tid = toast.loading("randomizzazione in corso…");
    try {
      const { data } = await api.post("/admin/randomize-dates", {
        match_date: m,
        only_published: true,
        from_date: "2021-01-01",
      });
      toast.success(`${data.updated} post aggiornati`, { id: tid });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore", { id: tid });
    }
  };

  return (
    <AdminShell
      title="Post"
      actions={
        <div className="flex items-center gap-2">
          <button
            data-testid="btn-bulk-author"
            onClick={bulkAuthor}
            title="Imposta in massa l'autore"
            className="hidden sm:flex items-center gap-2 border border-[#1a1918] text-[#1a1918] hover:bg-[#c5a873] hover:border-[#c5a873] transition-colors px-3 sm:px-4 py-2.5 font-sans text-[10px] sm:text-xs tracking-[0.25em] uppercase"
          >
            <UserPlus size={14} /> <span className="hidden md:inline">autore</span>
          </button>
          <button
            data-testid="btn-auto-cover"
            onClick={autoCoverAll}
            title="Assegna copertine casuali ai post che non ne hanno"
            className="hidden sm:flex items-center gap-2 border border-[#1a1918] text-[#1a1918] hover:bg-[#c5a873] hover:border-[#c5a873] transition-colors px-3 sm:px-4 py-2.5 font-sans text-[10px] sm:text-xs tracking-[0.25em] uppercase"
          >
            <Image size={14} /> <span className="hidden md:inline">copertine</span>
          </button>
          <button
            data-testid="btn-randomize-dates"
            onClick={randomizeDates}
            title="Randomizza la data dei post pubblicati con data 01/05/2026"
            className="hidden sm:flex items-center gap-2 border border-[#1a1918] text-[#1a1918] hover:bg-[#c5a873] hover:border-[#c5a873] transition-colors px-3 sm:px-4 py-2.5 font-sans text-[10px] sm:text-xs tracking-[0.25em] uppercase"
          >
            <Shuffle size={14} /> <span className="hidden md:inline">date</span>
          </button>
          {stats.drafts > 0 && (
            <button
              data-testid="btn-reformat-drafts"
              onClick={reformatAllDrafts}
              title="Riformatta capoversi di tutte le bozze"
              className="hidden sm:flex items-center gap-2 border border-[#1a1918] text-[#1a1918] hover:bg-[#d4b886] hover:border-[#d4b886] transition-colors px-3 sm:px-4 py-2.5 font-sans text-[10px] sm:text-xs tracking-[0.25em] uppercase"
            >
              <Wand2 size={14} /> <span className="hidden md:inline">riformatta bozze</span>
            </button>
          )}
          <Link
            to="/admin/new"
            data-testid="btn-new-post"
            className="flex items-center gap-2 bg-[#1a1918] text-[#f9f8f6] hover:bg-[#4a4843] transition-colors px-5 py-2.5 font-sans text-xs tracking-[0.25em] uppercase"
          >
            <Plus size={14} /> nuovo
          </Link>
        </div>
      }
    >
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6 mb-8 sm:mb-10 max-w-3xl">
        {[
          { k: "total", label: "totali", v: stats.total },
          { k: "published", label: "pubblicati", v: stats.published },
          { k: "drafts", label: "bozze", v: stats.drafts },
          { k: "to_review", label: "da controllare", v: stats.to_review, accent: true },
        ].map((s) => (
          <div key={s.k} data-testid={`stat-${s.k}`} className="border border-[#e5e1d8] p-4 sm:p-6">
            <div className={`font-serif text-3xl sm:text-4xl font-light ${s.accent ? "text-[#c5a873]" : "text-[#1a1918]"}`}>{s.v}</div>
            <div className="font-sans text-[10px] tracking-[0.25em] sm:tracking-[0.3em] uppercase text-[#4a4843] mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-4 mb-6">
        <div className="relative w-full">
          <Search size={14} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#4a4843]" />
          <input
            data-testid="admin-search"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="cerca per titolo…"
            className="w-full bg-transparent border-0 border-b border-[#1a1918] py-2 pl-6 font-sans text-sm focus:outline-none focus:border-[#d4b886]"
          />
        </div>
        <div className="flex gap-1 -mx-1 overflow-x-auto">
          <Tab data-testid="tab-drafts" active={status === "draft"} onClick={() => { setStatus("draft"); setPage(1); }}>bozze</Tab>
          <Tab data-testid="tab-published" active={status === "published"} onClick={() => { setStatus("published"); setPage(1); }}>pubblicati</Tab>
          <Tab data-testid="tab-review" active={status === "review"} onClick={() => { setStatus("review"); setPage(1); }}>
            <span className="inline-flex items-center gap-1.5">
              <Bookmark size={11} className="inline" /> da controllare{stats.to_review > 0 ? ` (${stats.to_review})` : ""}
            </span>
          </Tab>
          <Tab data-testid="tab-reserved" active={status === "reserved"} onClick={() => { setStatus("reserved"); setPage(1); }}>riservati</Tab>
        </div>
      </div>

      {status === "published" && authorStats.length > 0 && (
        <div data-testid="author-counter" className="mb-6 -mt-2 border border-[#e5e1d8] bg-[#f9f8f6] p-4">
          <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#4a4843] mb-3">
            Pubblicati per autore
          </p>
          <div className="flex flex-wrap gap-2">
            {authorStats.map((a) => (
              <span
                key={a.author}
                data-testid={`author-chip-${a.author}`}
                className="inline-flex items-center gap-2 border border-[#1a1918] bg-white px-3 py-1.5"
              >
                <span className="font-serif text-[#1a1918]">{a.author}</span>
                <span className="font-sans text-[10px] tracking-[0.2em] uppercase bg-[#1a1918] text-[#f9f8f6] px-2 py-0.5">
                  {a.count}
                </span>
              </span>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <p className="font-sans text-sm text-[#4a4843]">caricamento…</p>
      ) : items.length === 0 ? (
        <p data-testid="admin-empty" className="font-serif italic text-xl text-[#4a4843] py-16 text-center">
          Nessun post.
        </p>
      ) : (
        <>
          {/* Desktop table */}
          <table className="w-full hidden md:table" data-testid="admin-posts-table">
            <thead>
              <tr className="border-b border-[#e5e1d8]">
                <th className="text-left py-3 font-sans text-[10px] tracking-[0.3em] uppercase text-[#4a4843]">Titolo</th>
                <th className="text-left py-3 font-sans text-[10px] tracking-[0.3em] uppercase text-[#4a4843] hidden md:table-cell">Data</th>
                <th className="text-left py-3 font-sans text-[10px] tracking-[0.3em] uppercase text-[#4a4843]">Stato</th>
                <th className="text-right py-3 font-sans text-[10px] tracking-[0.3em] uppercase text-[#4a4843]">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} data-testid={`admin-row-${p.id}`} className="border-b border-[#e5e1d8] hover:bg-[#f2efe9] transition-colors">
                  <td className="py-4 pr-4">
                    <Link to={`/admin/edit/${p.id}`} className="font-serif text-lg text-[#1a1918] hover:text-[#4a4843]">
                      {p.title || "(senza titolo)"}
                    </Link>
                    {p.author && (
                      <div className="font-serif italic text-sm text-[#4a4843] mt-0.5">
                        di {p.author}
                      </div>
                    )}
                    {p.is_reserved && (
                      <div className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#9e4747] mt-1">
                        ◇ riservato a {p.reserved_for_username || "(nessun lettore)"}
                      </div>
                    )}
                    {p.source_filename && (
                      <div className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#4a4843] mt-1">
                        ↳ {p.source_filename}
                      </div>
                    )}
                  </td>
                  <td className="py-4 pr-4 hidden md:table-cell font-sans text-xs text-[#4a4843]">
                    {formatDate(p.created_at)}
                  </td>
                  <td className="py-4 pr-4">
                    <div
                      role="radiogroup"
                      data-testid={`row-state-${p.id}`}
                      className="inline-flex border border-[#1a1918] text-[10px] tracking-[0.2em] uppercase"
                    >
                      {(() => {
                        const cur = p.needs_review ? "review" : p.published ? "published" : "draft";
                        const opts = [
                          { k: "draft", label: "bozza", tone: "bg-[#1a1918] text-[#f9f8f6]" },
                          { k: "published", label: "pubbl.", tone: "bg-[#477d55] text-[#f9f8f6]" },
                          { k: "review", label: "ctrl", tone: "bg-[#c5a873] text-[#1a1918]" },
                        ];
                        return opts.map((s, i) => {
                          const active = cur === s.k;
                          return (
                            <button
                              key={s.k}
                              type="button"
                              role="radio"
                              aria-checked={active}
                              data-testid={`row-state-${p.id}-${s.k}`}
                              onClick={() => setStateValue(p.id, s.k)}
                              className={`px-2.5 py-1 transition-colors ${
                                i < opts.length - 1 ? "border-r border-[#1a1918]" : ""
                              } ${active ? s.tone : "bg-transparent text-[#1a1918] hover:bg-[#f2efe9]"}`}
                            >
                              {s.label}
                            </button>
                          );
                        });
                      })()}
                    </div>
                  </td>
                  <td className="py-4">
                    <div className="flex justify-end gap-1">
                      <Link
                        data-testid={`btn-edit-${p.id}`}
                        to={`/admin/edit/${p.id}`}
                        className="p-2 hover:bg-[#e5e1d8] transition-colors"
                        title="Modifica"
                      >
                        <Edit3 size={14} />
                      </Link>
                      <button
                        data-testid={`btn-delete-${p.id}`}
                        onClick={() => remove(p.id)}
                        title="Elimina"
                        className="p-2 hover:bg-[#9e4747] hover:text-[#f9f8f6] transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Mobile list */}
          <ul className="md:hidden divide-y divide-[#e5e1d8] border-t border-[#e5e1d8]" data-testid="admin-posts-list-mobile">
            {items.map((p) => (
              <li key={p.id} data-testid={`admin-row-${p.id}`} className="py-4">
                <Link to={`/admin/edit/${p.id}`} className="block pr-2">
                  <h3 className="font-serif text-xl leading-snug text-[#1a1918] mb-2">
                    {p.title || "(senza titolo)"}
                  </h3>
                  {p.author && (
                    <div className="font-serif italic text-sm text-[#4a4843] mb-1">
                      di {p.author}
                    </div>
                  )}
                  <div className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#4a4843]">
                    {formatDate(p.created_at)}{p.source_filename ? ` · ${p.source_filename}` : ""}
                  </div>
                </Link>
                <div
                  role="radiogroup"
                  data-testid={`row-state-${p.id}`}
                  className="mt-3 flex border border-[#1a1918] text-[10px] tracking-[0.2em] uppercase"
                >
                  {(() => {
                    const cur = p.needs_review ? "review" : p.published ? "published" : "draft";
                    const opts = [
                      { k: "draft", label: "bozza", tone: "bg-[#1a1918] text-[#f9f8f6]" },
                      { k: "published", label: "pubblicato", tone: "bg-[#477d55] text-[#f9f8f6]" },
                      { k: "review", label: "da controllare", tone: "bg-[#c5a873] text-[#1a1918]" },
                    ];
                    return opts.map((s, i) => {
                      const active = cur === s.k;
                      return (
                        <button
                          key={s.k}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          data-testid={`row-state-${p.id}-${s.k}`}
                          onClick={() => setStateValue(p.id, s.k)}
                          className={`flex-1 px-2 py-2.5 transition-colors ${
                            i < opts.length - 1 ? "border-r border-[#1a1918]" : ""
                          } ${active ? s.tone : "bg-transparent text-[#1a1918]"}`}
                        >
                          {s.label}
                        </button>
                      );
                    });
                  })()}
                </div>
                <div className="flex gap-2 mt-2">
                  <Link
                    data-testid={`btn-edit-${p.id}`}
                    to={`/admin/edit/${p.id}`}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-[#1a1918] text-[#f9f8f6] font-sans text-[10px] tracking-[0.25em] uppercase"
                  >
                    <Edit3 size={13} /> modifica
                  </Link>
                  <button
                    data-testid={`btn-delete-${p.id}`}
                    onClick={() => remove(p.id)}
                    className="p-2.5 border border-[#9e4747] text-[#9e4747]"
                    aria-label="elimina"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {totalPages > 1 && (
        <div className="mt-8 mb-24 sm:mb-12 flex items-center justify-between gap-3">
          <button
            data-testid="admin-prev"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="bg-[#1a1918] text-[#f9f8f6] hover:bg-[#4a4843] transition-colors px-4 sm:px-6 py-2.5 font-sans text-[10px] sm:text-xs tracking-[0.25em] uppercase disabled:opacity-30 disabled:cursor-not-allowed"
          >
            ← indietro
          </button>
          <span className="font-sans text-[10px] sm:text-xs tracking-[0.2em] uppercase text-[#4a4843] text-center">
            {page} / {totalPages} <span className="hidden sm:inline">— {total} post</span>
          </span>
          <button
            data-testid="admin-next"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="mr-0 sm:mr-32 bg-[#1a1918] text-[#f9f8f6] hover:bg-[#4a4843] transition-colors px-4 sm:px-6 py-2.5 font-sans text-[10px] sm:text-xs tracking-[0.25em] uppercase disabled:opacity-30 disabled:cursor-not-allowed"
          >
            avanti →
          </button>
        </div>
      )}
    </AdminShell>
  );
};

export default AdminPosts;
